import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import SelectWithSearch from "@components/common/SelectWithSearch/SelectWithSearch";
import { mergeSelectOption } from "../utils/statusUtils";

interface SelectOption {
	value: string;
	label: string;
}

interface EmploymentLocationDropdownsProps {
	index: number;
	disabled: boolean;
	showDistrictAndCity: boolean;
	countryValue: string;
	countryName?: string;
	districtValue: string;
	districtName?: string;
	cityValue: string;
	cityName?: string;
	countryOptions: SelectOption[];
	districtOptions: SelectOption[];
	cityOptions: SelectOption[];
	loadingCountries: boolean;
	loadingDistricts: boolean;
	loadingCities: boolean;
	onCountryChange: (uuid: string, countryName: string) => void;
	onDistrictChange: (uuid: string, districtName: string) => void;
	onCityChange: (uuid: string, cityName: string) => void;
	onCountrySearchChange: (term: string) => void;
	onDistrictSearchChange: (term: string) => void;
	onCitySearchChange: (term: string) => void;
	fetchNextCountries: () => void;
	fetchNextDistricts: () => void;
	fetchNextCities: () => void;
	hasNextCountries: boolean;
	hasNextDistricts: boolean;
	hasNextCities: boolean;
	isFetchingNextCountries: boolean;
	isFetchingNextDistricts: boolean;
	isFetchingNextCities: boolean;
}

const EmploymentLocationDropdowns = ({
	index,
	disabled,
	showDistrictAndCity,
	countryValue,
	countryName,
	districtValue,
	districtName,
	cityValue,
	cityName,
	countryOptions,
	districtOptions,
	cityOptions,
	loadingCountries,
	loadingDistricts,
	loadingCities,
	onCountryChange,
	onDistrictChange,
	onCityChange,
	onCountrySearchChange,
	onDistrictSearchChange,
	onCitySearchChange,
	fetchNextCountries,
	fetchNextDistricts,
	fetchNextCities,
	hasNextCountries,
	hasNextDistricts,
	hasNextCities,
	isFetchingNextCountries,
	isFetchingNextDistricts,
	isFetchingNextCities,
}: EmploymentLocationDropdownsProps) => {
	const { t } = useTranslation();

	const mergedCountryOptions = useMemo(
		() => mergeSelectOption(countryOptions, countryValue, countryName),
		[countryOptions, countryValue, countryName],
	);

	const mergedDistrictOptions = useMemo(
		() => mergeSelectOption(districtOptions, districtValue, districtName),
		[districtOptions, districtValue, districtName],
	);

	const mergedCityOptions = useMemo(
		() => mergeSelectOption(cityOptions, cityValue, cityName),
		[cityOptions, cityValue, cityName],
	);

	const handleCountrySelect = (uuid: string) => {
		const name =
			mergedCountryOptions.find(option => option.value === uuid)?.label ??
			countryOptions.find(option => option.value === uuid)?.label ??
			"";
		onCountryChange(uuid, name);
	};

	const handleDistrictSelect = (uuid: string) => {
		const name =
			mergedDistrictOptions.find(option => option.value === uuid)?.label ??
			districtOptions.find(option => option.value === uuid)?.label ??
			"";
		onDistrictChange(uuid, name);
	};

	const handleCitySelect = (uuid: string) => {
		const name =
			mergedCityOptions.find(option => option.value === uuid)?.label ??
			cityOptions.find(option => option.value === uuid)?.label ??
			"";
		onCityChange(uuid, name);
	};

	return (
		<>
			<SelectWithSearch
				id={`country-${index}`}
				label={t("feature.status.form.country")}
				placeholder={t("feature.status.form.selectCountry")}
				value={countryValue}
				onChange={handleCountrySelect}
				options={mergedCountryOptions}
				required
				disabled={disabled}
				loading={loadingCountries}
				isSearchEnabled
				searchPlaceholder={t("feature.status.form.searchCountries")}
				onSearchChange={onCountrySearchChange}
				onScrollToBottom={fetchNextCountries}
				hasNextPage={hasNextCountries}
				isFetchingNextPage={isFetchingNextCountries}
			/>
			{showDistrictAndCity && (
				<>
					<SelectWithSearch
						id={`district-${index}`}
						label={t("feature.status.form.district")}
						placeholder={t("feature.status.form.selectDistrict")}
						value={districtValue}
						onChange={handleDistrictSelect}
						options={mergedDistrictOptions}
						required
						disabled={disabled}
						loading={loadingDistricts}
						isSearchEnabled
						searchPlaceholder={t("feature.status.form.searchDistricts")}
						onSearchChange={onDistrictSearchChange}
						onScrollToBottom={fetchNextDistricts}
						hasNextPage={hasNextDistricts}
						isFetchingNextPage={isFetchingNextDistricts}
					/>
					<SelectWithSearch
						id={`city-${index}`}
						label={t("feature.status.form.city")}
						placeholder={
							districtValue
								? t("feature.status.form.selectCity")
								: t("feature.status.form.selectDistrictFirst")
						}
						value={cityValue}
						onChange={handleCitySelect}
						options={mergedCityOptions}
						required
						disabled={disabled || !districtValue}
						loading={loadingCities}
						isSearchEnabled
						searchPlaceholder={t("feature.status.form.searchCities")}
						onSearchChange={onCitySearchChange}
						onScrollToBottom={fetchNextCities}
						hasNextPage={hasNextCities}
						isFetchingNextPage={isFetchingNextCities}
					/>
				</>
			)}
		</>
	);
};

export default EmploymentLocationDropdowns;
